import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import DashboardLayout from "../components/DashboardLayout";
import { useLanguage } from "../context/LanguageContext";
import { 
  BookOpen, Target, Trophy, MapPin, Brain, FileText,
  ChevronRight, TrendingUp, CheckCircle2, Flame, 
  Calendar, Award, Zap, Layers
} from "lucide-react";
import { toast } from "sonner";

const BADGE_INFO = {
  first_day: { name: "First Steps", icon: "🌱", description: "Completed your first study session" },
  "3_day_streak": { name: "Getting Started", icon: "🔥", description: "3-day study streak" },
  week_warrior: { name: "Week Warrior", icon: "⚔️", description: "7-day study streak" },
  fortnight_fighter: { name: "Fortnight Fighter", icon: "🛡️", description: "14-day study streak" },
  monthly_master: { name: "Monthly Master", icon: "👑", description: "30-day study streak" }
};

const Dashboard = ({ auth }) => {
  const { t } = useLanguage();
  const [progress, setProgress] = useState(null);
  const [recentTests, setRecentTests] = useState([]);
  const [streak, setStreak] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loggingStreak, setLoggingStreak] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [progressRes, historyRes, streakRes] = await Promise.all([
        axios.get(`${API}/progress`, {
          withCredentials: true,
          headers: auth.token ? { Authorization: `Bearer ${auth.token}` } : {}
        }),
        axios.get(`${API}/tests/history`, {
          withCredentials: true,
          headers: auth.token ? { Authorization: `Bearer ${auth.token}` } : {}
        }),
        axios.get(`${API}/streak`, {
          withCredentials: true,
          headers: auth.token ? { Authorization: `Bearer ${auth.token}` } : {}
        })
      ]);
      setProgress(progressRes.data);
      setRecentTests(historyRes.data.slice(0, 3));
      setStreak(streakRes.data);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const logStudyActivity = async () => {
    setLoggingStreak(true);
    try {
      const response = await axios.post(`${API}/streak/log`, {}, {
        withCredentials: true,
        headers: auth.token ? { Authorization: `Bearer ${auth.token}` } : {}
      });
      
      setStreak(prev => ({
        ...prev,
        current_streak: response.data.current_streak,
        longest_streak: response.data.longest_streak
      }));

      if (response.data.new_badge) {
        const badge = BADGE_INFO[response.data.new_badge];
        toast.success(`${badge.icon} New Badge Earned: ${badge.name}!`, {
          description: badge.description
        });
      } else {
        toast.success(response.data.message);
      }
    } catch (error) {
      toast.error("Failed to log activity");
    } finally {
      setLoggingStreak(false);
    }
  };

  const quickActions = [
    { 
      icon: <BookOpen className="w-6 h-6" />, 
      title: t('nav.studyGuide'), 
      description: t('landing.featuresSection.feature1Desc').substring(0, 30) + '...',
      link: "/study",
      color: "bg-blue-500"
    },
    { 
      icon: <Target className="w-6 h-6" />, 
      title: t('nav.chapterPractice'), 
      description: t('landing.featuresSection.feature1'),
      link: "/chapter-practice",
      color: "bg-green-500"
    },
    { 
      icon: <Zap className="w-6 h-6" />, 
      title: t('nav.simulations'), 
      description: "30 progressive tests",
      link: "/simulations",
      color: "bg-amber-500"
    },
    { 
      icon: <Trophy className="w-6 h-6" />, 
      title: t('nav.mockTests'), 
      description: "Realistic exam practice",
      link: "/mock-tests",
      color: "bg-maple"
    },
    { 
      icon: <Layers className="w-6 h-6" />, 
      title: t('nav.flashcards'), 
      description: t('landing.featuresSection.feature2'),
      link: "/flashcards",
      color: "bg-purple-500"
    },
    { 
      icon: <Calendar className="w-6 h-6" />, 
      title: t('nav.studyPlanner'), 
      description: t('landing.featuresSection.feature4Desc').substring(0, 25) + '...',
      link: "/journey",
      color: "bg-teal-500"
    }
  ];

  const stats = [
    {
      label: t('dashboard.testsTaken'),
      value: progress?.tests_taken || 0,
      icon: <FileText className="w-5 h-5" />,
      color: "text-blue-600 bg-blue-100"
    },
    {
      label: t('dashboard.testsPassed'),
      value: progress?.tests_passed || 0,
      icon: <CheckCircle2 className="w-5 h-5" />,
      color: "text-green-600 bg-green-100"
    },
    {
      label: t('dashboard.averageScore'),
      value: `${progress?.average_score || 0}%`,
      icon: <TrendingUp className="w-5 h-5" />,
      color: "text-maple bg-maple-50"
    },
    {
      label: t('dashboard.studyStreak'),
      value: `${streak?.current_streak || 0} ${t('dashboard.studyDays')}`,
      icon: <Flame className="w-5 h-5" />,
      color: "text-orange-600 bg-orange-100"
    }
  ];

  if (loading) {
    return (
      <DashboardLayout auth={auth}>
        <div className="flex items-center justify-center h-64">
          <div className="w-12 h-12 border-4 border-maple border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout auth={auth}>
      <div className="space-y-6" data-testid="dashboard-content">
        {/* Welcome Section */}
        <div className="bg-gradient-to-r from-maple to-maple-600 rounded-2xl p-6 text-white">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="font-heading text-2xl md:text-3xl font-bold mb-1">
                {t('dashboard.welcomeBack')}, {auth.user?.name?.split(" ")[0] || "there"}! 👋
              </h1>
              <p className="text-white/90">
                {progress?.tests_taken > 0 
                  ? `You've completed ${progress.tests_taken} tests. Keep practicing!`
                  : t('dashboard.readyToStart')}
              </p>
            </div>
            <Link to="/chapter-practice">
              <Button className="bg-white text-maple hover:bg-slate-100 rounded-full px-6" data-testid="start-practice-btn">
                {t('dashboard.startPractice')}
                <ChevronRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>

        {/* Study Streak Card */}
        <Card className="border-2 border-orange-200 bg-gradient-to-r from-orange-50 to-amber-50" data-testid="streak-card">
          <CardContent className="p-5">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-gradient-to-br from-orange-400 to-red-500 rounded-2xl flex items-center justify-center shadow-lg">
                  <Flame className="w-8 h-8 text-white" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-3xl font-heading font-bold text-slate-900">
                      {streak?.current_streak || 0}
                    </span>
                    <span className="text-slate-600">{t('dashboard.dayStreak')}</span>
                    {streak?.current_streak >= 3 && (
                      <span className="text-xl">🔥</span>
                    )}
                  </div>
                  <p className="text-sm text-slate-600">
                    {t('dashboard.longest')}: {streak?.longest_streak || 0} • {t('dashboard.total')}: {streak?.total_study_days || 0} {t('dashboard.studyDays')}
                  </p>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                {/* Badges */}
                {streak?.badges?.length > 0 && (
                  <div className="flex gap-1">
                    {streak.badges.slice(-4).map((badge, idx) => (
                      <div 
                        key={idx} 
                        className="w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-sm border border-slate-200"
                        title={BADGE_INFO[badge]?.name}
                      >
                        <span className="text-sm">{BADGE_INFO[badge]?.icon}</span>
                      </div>
                    ))}
                  </div>
                )}
                
                <Button 
                  onClick={logStudyActivity}
                  disabled={loggingStreak}
                  className="bg-orange-500 hover:bg-orange-600 text-white rounded-full px-5"
                  data-testid="log-streak-btn"
                >
                  {loggingStreak ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <Flame className="w-4 h-4 mr-2" />
                      {t('dashboard.logTodaysStudy')}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map((stat, index) => (
            <Card key={index} className="border-0 shadow-sm" data-testid={`stat-card-${index}`}>
              <CardContent className="p-5">
                <div className={`w-10 h-10 rounded-lg ${stat.color} flex items-center justify-center mb-2`}>
                  {stat.icon}
                </div>
                <p className="text-xl font-heading font-bold text-slate-900">{stat.value}</p>
                <p className="text-sm text-slate-600">{stat.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Quick Actions */}
        <div>
          <h2 className="font-heading text-lg font-semibold text-slate-900 mb-3">{t('dashboard.quickActions')}</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {quickActions.map((action, index) => (
              <Link to={action.link} key={index}>
                <Card className="border border-slate-200 hover:border-maple/50 hover:shadow-md transition-all cursor-pointer h-full" data-testid={`quick-action-${index}`}>
                  <CardContent className="p-4 text-center">
                    <div className={`w-11 h-11 ${action.color} rounded-xl flex items-center justify-center text-white mb-3 mx-auto`}>
                      {action.icon}
                    </div>
                    <h3 className="font-heading font-semibold text-slate-900 text-sm mb-0.5">{action.title}</h3>
                    <p className="text-xs text-slate-600">{action.description}</p>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Recent Tests */}
          <Card className="border-0 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="font-heading text-lg">{t('dashboard.recentTests')}</CardTitle>
              <Link to="/analytics" className="text-sm text-maple hover:underline">{t('dashboard.viewAll')}</Link>
            </CardHeader>
            <CardContent>
              {recentTests.length > 0 ? (
                <div className="space-y-3">
                  {recentTests.map((test, index) => {
                    const testDate = test.completed_at || test.created_at;
                    const formattedDate = testDate ? new Date(testDate).toLocaleDateString() : 'N/A';
                    return (
                    <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center ${test.passed ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}>
                          {test.passed ? <CheckCircle2 className="w-4 h-4" /> : <Target className="w-4 h-4" />}
                        </div>
                        <div>
                          <p className="font-medium text-slate-900 text-sm">Test</p>
                          <p className="text-xs text-slate-600">
                            {formattedDate}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`font-heading font-bold text-sm ${test.passed ? 'text-green-600' : 'text-red-600'}`}>
                          {test.score}%
                        </p>
                        <p className="text-xs text-slate-600">
                          {test.correct}/{test.total_questions || test.total || 20}
                        </p>
                      </div>
                    </div>
                  )})}
                </div>
              ) : (
                <div className="text-center py-6 text-slate-500">
                  <Trophy className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No tests taken yet</p>
                  <Link to="/simulations">
                    <Button variant="link" className="text-maple text-sm">{t('tests.startTest')}</Button>
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Progress Overview */}
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-lg">{t('dashboard.yourProgress')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-slate-600">{t('dashboard.overallReadiness')}</span>
                  <span className="font-medium text-slate-900">{progress?.average_score || 0}%</span>
                </div>
                <Progress value={progress?.average_score || 0} className="h-2.5" />
                <p className="text-xs text-slate-500 mt-1">Pass score: 75%</p>
              </div>

              <div className={`p-4 rounded-lg ${progress?.average_score >= 75 ? 'bg-green-50' : 'bg-maple-50'}`}>
                <div className="flex items-start gap-3">
                  <Brain className={`w-5 h-5 mt-0.5 ${progress?.average_score >= 75 ? 'text-green-600' : 'text-maple'}`} />
                  <div>
                    <p className="font-medium text-slate-900 text-sm mb-1">
                      {progress?.average_score >= 75 
                        ? "You're ready for the test!" 
                        : "Keep practicing!"}
                    </p>
                    <p className="text-xs text-slate-600">
                      {progress?.average_score >= 75
                        ? "Your scores show strong preparation. Consider taking a mock test."
                        : "Continue studying to reach the 75% passing threshold."}
                    </p>
                  </div>
                </div>
              </div>

              {auth.user?.subscription_tier === "free" && (
                <div className="p-4 bg-gradient-to-r from-yellow-50 to-amber-50 rounded-lg border border-yellow-200">
                  <div className="flex items-start gap-3">
                    <Award className="w-5 h-5 text-yellow-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-slate-900 text-sm mb-1">{t('common.upgrade')}</p>
                      <p className="text-xs text-slate-600 mb-2">
                        {t('landing.unlockQuestions')}
                      </p>
                      <Link to="/pricing">
                        <Button size="sm" className="bg-yellow-500 hover:bg-yellow-600 text-white text-xs h-8">
                          {t('pricing.lifetime')} - $14.99
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Dashboard;
